import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { AppLayout } from '../layouts/AppLayout';
import { Card, Badge, Select, Button } from '../components/ui';
import { useJobs, JobRecord } from '../hooks/useJobs';
import { useTemplates, Template } from '../hooks/useTemplates';
import { MARKETS } from '../lib/markets';
import { getJobDisplayTitle } from '../lib/jobDisplayTitle';
import { db } from '../lib/firebase';
import { Download, Play, Clock, RefreshCw, AlertTriangle } from 'lucide-react';

const JOBS_MARKETS = [
  { value: 'all', label: 'All Markets' },
  ...MARKETS,
];

const JOBS_SOURCES = [
  { value: 'all', label: 'All' },
  { value: 'manual', label: 'Manual' },
  { value: 'pipeline', label: 'Pipeline' },
];

function buildTemplateOptions(templates: Template[]) {
  return [
    { value: 'all', label: 'All Templates' },
    ...templates.map((t) => ({ value: t.id, label: t.name })),
  ];
}

function statusVariant(
  status: JobRecord['status'],
): 'success' | 'error' | 'warning' | 'info' | 'default' {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'queued') return 'warning';
  if (status === 'pending_assets') return 'warning';
  if (status === 'draft') return 'warning';
  if (status === 'rendering' || status === 'preprocessing' || status === 'uploading') return 'info';
  return 'default';
}

function statusLabel(status: JobRecord['status']): string {
  const labels: Record<string, string> = {
    queued: 'Queued',
    preprocessing: 'Preprocessing',
    rendering: 'Rendering',
    uploading: 'Uploading',
    completed: 'Completed',
    failed: 'Failed',
    pending_assets: 'Pending Assets',
    draft: 'Draft',
  };
  return labels[status] ?? status;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) + ' ' + date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

interface JobRowProps {
  job: JobRecord;
  template?: Template;
  /** When true, a highlight ring is shown and the row is scrolled into view. */
  highlighted?: boolean;
}

function JobRow({ job, template, highlighted = false }: JobRowProps) {
  const navigate = useNavigate();
  const rowRef = useRef<HTMLDivElement>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  const templateName = template?.name ?? job.templateId;
  const displayTitle = getJobDisplayTitle(job, template);
  const outputUrl = job.previewVideoUrl ?? job.outputVideoUrl;
  const isCompleted = job.status === 'completed';
  const isFailed = job.status === 'failed';
  const isPendingAssets = job.status === 'pending_assets';

  // Scroll highlighted job into view once it is rendered.
  useEffect(() => {
    if (highlighted && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlighted]);
  const errorMsg =
    job.error == null
      ? null
      : typeof job.error === 'string'
      ? job.error
      : job.error.message;

  /**
   * Download the video as a real file download.
   *
   * The `download` attribute on a plain `<a>` is ignored by browsers for
   * cross-origin URLs (Firebase Storage is a different origin), so the file
   * opens in a new tab instead of downloading. Fetching the video as a blob
   * and creating a same-origin blob URL makes the `download` attribute work
   * in every browser.
   *
   */
  const handleDownload = async () => {
    if (!outputUrl || downloading) return;
    const filename = job.name ? `${job.name}.mp4` : `job-${job.id}.mp4`;
    setDownloadError(null);
    setDownloading(true);
    console.log(`[download] starting — url="${outputUrl}" filename="${filename}"`);
    try {
      console.log('[download] fetch start');
      const response = await fetch(outputUrl);
      console.log(`[download] fetch done — status=${response.status} ok=${response.ok}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      console.log('[download] reading blob');
      const blob = await response.blob();
      console.log(`[download] blob ready — size=${blob.size} type="${blob.type}"`);

      const blobUrl = URL.createObjectURL(blob);
      console.log(`[download] objectURL created — blobUrl="${blobUrl}"`);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      console.log('[download] anchor appended, calling click()');
      a.click();
      document.body.removeChild(a);

      // Delay revoke so the browser has time to begin reading the blob before
      // the URL is invalidated — revoking synchronously after click() races
      // against the download and silently produces an empty / missing file.
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
        console.log('[download] objectURL revoked');
      }, 100);

      console.log('[download] done');
    } catch (error) {
      console.error('[download] error', error);
      const detail = error instanceof Error ? ` (${error.message})` : '';
      setDownloadError(
        `Download failed${detail}. Please try again.`,
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleRetry = async () => {
    if (!isFailed || retrying) return;
    setRetryError(null);
    setRetrying(true);
    try {
      await updateDoc(doc(db, 'jobs', job.id), {
        status: 'queued',
        progress: 0,
        error: null,
        lastError: null,
        retriedAt: serverTimestamp(),
        outputVideoUrl: null,
        previewVideoUrl: null,
        outputPath: null,
        completedAt: null,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      const detail = error instanceof Error ? ` (${error.message})` : '';
      setRetryError(`Retry failed${detail}. Please try again.`);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div
      ref={rowRef}
      className={`border rounded-md overflow-hidden transition-shadow ${highlighted ? 'border-accent-red ring-2 ring-accent-red' : 'border-border-subtle'}`}
    >
      <div className="flex items-start justify-between px-4 py-3 bg-bg-secondary gap-4">
        {/* Left: template + meta */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary truncate">{displayTitle}</p>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className="flex items-center gap-1 text-xs text-text-tertiary">
              <Clock className="w-3 h-3" />
              {formatDate(job.createdAt)}
            </span>
            {job.market && job.market !== 'default' && (
              <span className="text-xs text-text-tertiary capitalize">{job.market}</span>
            )}
            {job.jobSource === 'pipeline' ? (
              <Badge size="sm" variant="info">pipeline</Badge>
            ) : (
              <Badge size="sm" variant="default">manual</Badge>
            )}
            {job.jobSource === 'pipeline' && (
              <Badge size="sm" variant="default">{templateName}</Badge>
            )}
            {job.deliverableId && (
              <Badge size="sm" variant="default">{job.deliverableId}</Badge>
            )}
          </div>
          {errorMsg && (
            <p className="mt-1 text-xs text-accent-red line-clamp-2">{errorMsg}</p>
          )}
          {isPendingAssets && (job.missing_required_slots?.length ?? 0) > 0 && (
            <p className="mt-1 text-xs text-yellow-600">
              {job.missing_required_slots!.length} required slot{job.missing_required_slots!.length === 1 ? '' : 's'} missing
            </p>
          )}
          {downloadError && (
            <p className="mt-1 text-xs text-accent-red line-clamp-2">{downloadError}</p>
          )}
          {retryError && (
            <p className="mt-1 text-xs text-accent-red line-clamp-2">{retryError}</p>
          )}
        </div>

        {/* Right: status + actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge size="sm" variant={statusVariant(job.status)}>
            {statusLabel(job.status)}
          </Badge>

          {isPendingAssets && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(`/jobs/${job.id}`)}
              className="flex items-center gap-1.5"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              Resolve
            </Button>
          )}

          {isCompleted && outputUrl && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowPreview((v) => !v)}
                className="flex items-center gap-1.5"
              >
                <Play className="w-3.5 h-3.5" />
                {showPreview ? 'Hide' : 'Preview'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDownload}
                disabled={downloading}
                isLoading={downloading}
                className="flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" />
                {downloading ? 'Downloading…' : 'Download'}
              </Button>
            </>
          )}

          {isFailed && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRetry}
              disabled={retrying}
              isLoading={retrying}
              className="flex items-center gap-1.5"
            >
              {retrying ? 'Retrying…' : 'Retry'}
            </Button>
          )}
        </div>
      </div>

      {/* Inline video preview */}
      {showPreview && outputUrl && (
        <div className="px-4 pb-4 bg-bg-primary border-t border-border-subtle">
          <video
            src={outputUrl}
            controls
            className="mt-3 w-full max-w-xs rounded-md border border-border-subtle"
            aria-label={`Preview for job ${job.id}`}
          />
        </div>
      )}
    </div>
  );
}

function JobsPage() {
  const [searchParams] = useSearchParams();
  const highlightedJobId = searchParams.get('job');
  const [selectedMarket, setSelectedMarket] = useState<string>('all');
  const [selectedSource, setSelectedSource] = useState<string>('all');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('all');
  const { jobs, loading, error } = useJobs(selectedMarket);
  const { templates } = useTemplates();

  // Build a quick lookup of templateId → template metadata.
  const templateById: Record<string, Template> = {};
  for (const t of templates) {
    templateById[t.id] = t;
  }

  // Client-side source filter ('all' shows everything)
  let filteredJobs =
    selectedSource === 'all'
      ? jobs
      : jobs.filter((j) => (j.jobSource ?? 'manual') === selectedSource);

  // Client-side template filter ('all' shows everything)
  filteredJobs =
    selectedTemplate === 'all'
      ? filteredJobs
      : filteredJobs.filter((j) => j.templateId === selectedTemplate);

  return (
    <AppLayout
      title="Job History"
      subtitle="Track your video generation jobs and download completed videos."
    >
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Filters */}
        <Card>
          <div className="flex items-center gap-4 flex-wrap">
            <label className="text-sm font-medium text-text-primary">Market:</label>
            <Select
              value={selectedMarket}
              onChange={setSelectedMarket}
              options={JOBS_MARKETS}
              className="w-48"
            />
            <label className="text-sm font-medium text-text-primary">Template:</label>
            <Select
              value={selectedTemplate}
              onChange={setSelectedTemplate}
              options={buildTemplateOptions(templates)}
              className="w-48"
            />
            <label className="text-sm font-medium text-text-primary">Source:</label>
            <Select
              value={selectedSource}
              onChange={setSelectedSource}
              options={JOBS_SOURCES}
              className="w-36"
            />
            <span className="text-xs text-text-tertiary ml-auto">
              {loading ? (
                <RefreshCw className="w-3.5 h-3.5 animate-spin inline" />
              ) : (
                `${filteredJobs.length} job${filteredJobs.length === 1 ? '' : 's'}`
              )}
            </span>
          </div>
        </Card>

        {/* Error state */}
        {error && (
          <Card>
            <p className="text-sm text-accent-red">Failed to load jobs: {error}</p>
          </Card>
        )}

        {/* Empty state */}
        {!loading && !error && filteredJobs.length === 0 && (
          <Card className="text-center py-10">
            <p className="text-text-secondary text-sm">No jobs found for this market.</p>
            <p className="text-text-tertiary text-xs mt-1">
              Jobs created in the Generate page will appear here.
            </p>
          </Card>
        )}

        {/* Job list */}
        {filteredJobs.length > 0 && (
          <div className="space-y-2">
            {filteredJobs.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                template={templateById[job.templateId]}
                highlighted={job.id === highlightedJobId}
              />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

export default JobsPage;
